<template>
  <div class="jumbotron flex">
    <div
      :style="containerStyle"
      :class="['jumbotron-wrapper transparent', $props.color, $props.direction, $props.position]"
      ref="Wrapper"
    >
      <div :class="['jumbotron-wrapper-colored flex', $props.color, $props.position]">
        <div class="jumbotron-wrapper-colored-text">
          <h1
            v-if="!!$props.title"
            :class="['jumbotron-wrapper-colored-text-title text-center transparent', $props.color]"
            ref="Title"
          >
            {{ $props.title }}
          </h1>
          <p
            v-if="!!$props.paragraph"
            class="jumbotron-wrapper-colored-text-paragraph text-justify transparent"
            ref="Paragraph"
          >
            {{ $props.paragraph }}
          </p>
        </div>
      </div>
      <div :class="['jumbotron-wrapper-transparent flex', $props.position]">
        <img
          v-if="$props.displayImage"
          :src="$props.displayImage"
          :alt="$props.displayImageAlt"
          ref="DisplayImage"
          class="jumbotron-wrapper-transparent-image transparent"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Jumbotron",
  props: {
    /*
     * @property - title
     * Sets title of component
     */
    title: {
      type: String,
      default: ""
    },
    /*
     * @property - paragraph
     * Sets first paragraph of component
     */
    paragraph: {
      type: String,
      default: ""
    },
    /*
     * @property - displayImage
     * Sets specific displayImage for transparent side of jumbotron.
     */
    displayImage: {
      type: String,
      default: null
    },
    /*
     * @property - displayImageAlt
     * Sets specific displayImage alt for transparent side of jumbotron.
     */
    displayImageAlt: {
      type: String,
      default: ""
    },
    /*
     * @property - backgroundImage
     * Sets specific backgroundImage for transparent side of jumbotron.
     */
    backgroundImage: {
      type: String,
      default: null
    },
    /*
     * @property - color
     * Sets color style for background
     */
    color: {
      type: String,
      default: "primary"
    },
    /*
     * @property - direction
     * Sets direction style for background
     */
    direction: {
      type: String,
      default: "bottom"
    },
    /*
     * @property - position
     * Sets position style for background
     */
    position: {
      type: String,
      default: "left"
    }
  },
  computed: {
    /*
     * @computed - containerStyle
     * Returns current background image to use
     */
    containerStyle() {
      const style = {};

      if (this.$props.backgroundImage) {
        style.backgroundImage = `url(${this.$props.backgroundImage})`;
      }
      return style;
    },
    /*
     * @computed - directionStep
     * Returns current direction step according component direction
     */
    directionStep() {
      return this.$props.position === "left" ? 1 : -1;
    }
  },
  methods: {
    /*
     * @function - animateEntry
     * Calls m_animateEntry to animate component according the configuration sended, to work in both mobile and desktop versions
     * @param - none
     */
    async animateEntry() {
      const { Wrapper, Title, Paragraph, DisplayImage } = this.$refs;
      const { large } = this.$utils.styles.breakpoints;
      const animationConfig = [
        {
          component: Wrapper,
          method: "animateComponent",
          operationBreakpoint: "LARGEROREQUAL",
          behavior: 65,
          boundary: large
        },
        {
          component: Title,
          method: "animateTitle",
          operationBreakpoint: "SHORTER",
          behavior: "top",
          boundary: large
        },
        {
          component: Paragraph,
          method: "animateParagraph",
          operationBreakpoint: "SHORTER",
          behavior: 85,
          boundary: large
        }
      ];

      if (DisplayImage) {
        animationConfig.push({
          component: DisplayImage,
          method: "animateDisplayImage",
          operationBreakpoint: "SHORTER",
          behavior: 85,
          boundary: large
        });
      }
      await this.m_animateFade(Wrapper, 1000, "in");
      this.m_animateEntry(animationConfig);
    },
    /*
     * @function - animateComponent
     * Calls Jumbotron elements fade and translate stepped animation
     * @param - none
     */
    animateComponent() {
      this.animateTitle(0);
      this.animateParagraph(250);
      this.animateDisplayImage(500);
    },
    /*
     * @function - animateTitle
     * Calls Title element fade and translate stepped animation
     * @param - externalDelay (Number): Defines delay in milliseconds for animation
     */
    animateTitle(animationDelay = 0) {
      const { Title } = this.$refs;

      this.m_animateTranslation(
        Title,
        250,
        this.directionStep * 100,
        0,
        0,
        0,
        animationDelay,
        false
      );
      this.m_animateFade(Title, 500, "in", animationDelay);
    },
    /*
     * @function - animateParagraph
     * Calls Paragraph element fade and translate stepped animation
     * @param - externalDelay (Number): Defines delay in milliseconds for animation
     */
    animateParagraph(animationDelay = 0) {
      const { Paragraph } = this.$refs;

      this.m_animateTranslation(
        Paragraph,
        250,
        this.directionStep * 100,
        0,
        0,
        0,
        animationDelay,
        false
      );
      this.m_animateFade(Paragraph, 500, "in", animationDelay);
    },
    /*
     * @function - animateDisplayImage
     * Calls DisplayImage element fade and translate stepped animation
     * @param - externalDelay (Number): Defines delay in milliseconds for animation
     */
    animateDisplayImage(animationDelay = 0) {
      const { DisplayImage } = this.$refs;

      if (DisplayImage) {
        this.m_animateTranslation(
          DisplayImage,
          250,
          -this.directionStep * 100,
          0,
          0,
          0,
          animationDelay,
          false
        );
        this.m_animateFade(DisplayImage, 500, "in", animationDelay);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.jumbotron {
  margin: -1.5rem -0.75rem 0 -0.75rem;
  overflow: hidden;

  @include breakpoint($small) {
    margin: -2rem -1.25rem 0 0;
  }

  &-wrapper {
    @include boxShadow(0.65, true, false);
    background: $dark-gray no-repeat fixed;
    background-position: center;
    background-size: cover;
    display: inherit;
    flex-wrap: wrap;
    min-height: 55rem;
    position: relative;
    width: 100%;
    z-index: 0;

    @include breakpoint($small) {
      min-height: 65rem;
    }

    @include breakpoint($medium) {
      min-height: 50rem;
    }

    @include breakpoint($large) {
      min-height: 60vh;
    }

    &::before {
      content: "";
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
    }

    &.primary.bottom.left::before {
      background-image: linear-gradient(175deg, rgba($main-blue, 0.95) 55%, transparent 45%);

      @include breakpoint($large) {
        background-image: linear-gradient(85deg, rgba($main-blue, 0.95) 55%, transparent 45%);
      }

      @include breakpoint($xlarge) {
        background-image: linear-gradient(82.5deg, rgba($main-blue, 0.95) 55%, transparent 45%);
      }
    }

    &.primary.top.left::before {
      background-image: linear-gradient(185deg, rgba($main-blue, 0.95) 55%, transparent 45%);

      @include breakpoint($large) {
        background-image: linear-gradient(95deg, rgba($main-blue, 0.95) 55%, transparent 45%);
      }

      @include breakpoint($xlarge) {
        background-image: linear-gradient(97.5deg, rgba($main-blue, 0.95) 55%, transparent 45%);
      }
    }

    &.primary.doubled.left::before {
      background-image: linear-gradient(175deg, rgba($main-blue, 0.75) 55%, transparent 45%),
        linear-gradient(185deg, rgba($main-blue, 0.75) 55%, transparent 45%);

      @include breakpoint($large) {
        background-image: linear-gradient(85deg, rgba($main-blue, 0.75) 55%, transparent 45%),
          linear-gradient(95deg, rgba($main-blue, 0.75) 55%, transparent 45%);
      }

      @include breakpoint($xlarge) {
        background-image: linear-gradient(82.5deg, rgba($main-blue, 0.75) 55%, transparent 45%),
          linear-gradient(97.5deg, rgba($main-blue, 0.75) 55%, transparent 45%);
      }
    }

    &.primary.bottom.right::before {
      background-image: linear-gradient(5deg, rgba($main-blue, 0.95) 55%, transparent 45%);

      @include breakpoint($large) {
        background-image: linear-gradient(-85deg, rgba($main-blue, 0.95) 55%, transparent 45%);
      }

      @include breakpoint($xlarge) {
        background-image: linear-gradient(-82.5deg, rgba($main-blue, 0.95) 55%, transparent 45%);
      }
    }

    &.primary.top.right::before {
      background-image: linear-gradient(-5deg, rgba($main-blue, 0.95) 55%, transparent 45%);

      @include breakpoint($large) {
        background-image: linear-gradient(-95deg, rgba($main-blue, 0.95) 55%, transparent 45%);
      }

      @include breakpoint($xlarge) {
        background-image: linear-gradient(-97.5deg, rgba($main-blue, 0.95) 55%, transparent 45%);
      }
    }

    &.primary.doubled.right::before {
      background-image: linear-gradient(5deg, rgba($main-blue, 0.75) 55%, transparent 45%),
        linear-gradient(-5deg, rgba($main-blue, 0.75) 55%, transparent 45%);

      @include breakpoint($large) {
        background-image: linear-gradient(-85deg, rgba($main-blue, 0.75) 55%, transparent 45%),
          linear-gradient(-95deg, rgba($main-blue, 0.75) 55%, transparent 45%);
      }

      @include breakpoint($xlarge) {
        background-image: linear-gradient(-82.5deg, rgba($main-blue, 0.75) 55%, transparent 45%),
          linear-gradient(-97.5deg, rgba($main-blue, 0.75) 55%, transparent 45%);
      }
    }

    &.secondary.bottom.left::before {
      background-image: linear-gradient(175deg, rgba($main-green, 0.95) 55%, transparent 45%);

      @include breakpoint($large) {
        background-image: linear-gradient(85deg, rgba($main-green, 0.95) 55%, transparent 45%);
      }

      @include breakpoint($xlarge) {
        background-image: linear-gradient(82.5deg, rgba($main-green, 0.95) 55%, transparent 45%);
      }
    }

    &.secondary.top.left::before {
      background-image: linear-gradient(185deg, rgba($main-green, 0.95) 55%, transparent 45%);

      @include breakpoint($large) {
        background-image: linear-gradient(95deg, rgba($main-green, 0.95) 55%, transparent 45%);
      }

      @include breakpoint($xlarge) {
        background-image: linear-gradient(97.5deg, rgba($main-green, 0.95) 55%, transparent 45%);
      }
    }

    &.secondary.doubled.left::before {
      background-image: linear-gradient(175deg, rgba($main-green, 0.75) 55%, transparent 45%),
        linear-gradient(185deg, rgba($main-green, 0.75) 55%, transparent 45%);

      @include breakpoint($large) {
        background-image: linear-gradient(85deg, rgba($main-green, 0.75) 55%, transparent 45%),
          linear-gradient(95deg, rgba($main-green, 0.75) 55%, transparent 45%);
      }

      @include breakpoint($xlarge) {
        background-image: linear-gradient(82.5deg, rgba($main-green, 0.75) 55%, transparent 45%),
          linear-gradient(97.5deg, rgba($main-green, 0.75) 55%, transparent 45%);
      }
    }

    &.secondary.bottom.right::before {
      background-image: linear-gradient(5deg, rgba($main-green, 0.95) 55%, transparent 45%);

      @include breakpoint($large) {
        background-image: linear-gradient(-85deg, rgba($main-green, 0.95) 55%, transparent 45%);
      }

      @include breakpoint($xlarge) {
        background-image: linear-gradient(-82.5deg, rgba($main-green, 0.95) 55%, transparent 45%);
      }
    }

    &.secondary.top.right::before {
      background-image: linear-gradient(-5deg, rgba($main-green, 0.95) 55%, transparent 45%);

      @include breakpoint($large) {
        background-image: linear-gradient(-95deg, rgba($main-green, 0.95) 55%, transparent 45%);
      }

      @include breakpoint($xlarge) {
        background-image: linear-gradient(-97.5deg, rgba($main-green, 0.95) 55%, transparent 45%);
      }
    }

    &.secondary.doubled.right::before {
      background-image: linear-gradient(5deg, rgba($main-green, 0.75) 55%, transparent 45%),
        linear-gradient(-5deg, rgba($main-green, 0.75) 55%, transparent 45%);

      @include breakpoint($large) {
        background-image: linear-gradient(-85deg, rgba($main-green, 0.75) 55%, transparent 45%),
          linear-gradient(-95deg, rgba($main-green, 0.75) 55%, transparent 45%);
      }

      @include breakpoint($xlarge) {
        background-image: linear-gradient(-82.5deg, rgba($main-green, 0.75) 55%, transparent 45%),
          linear-gradient(-97.5deg, rgba($main-green, 0.75) 55%, transparent 45%);
      }
    }

    &.tertiary.bottom.left::before {
      background-image: linear-gradient(175deg, rgba($live-blue, 0.95) 55%, transparent 45%);

      @include breakpoint($large) {
        background-image: linear-gradient(85deg, rgba($live-blue, 0.95) 55%, transparent 45%);
      }

      @include breakpoint($xlarge) {
        background-image: linear-gradient(82.5deg, rgba($live-blue, 0.95) 55%, transparent 45%);
      }
    }

    &.tertiary.top.left::before {
      background-image: linear-gradient(185deg, rgba($live-blue, 0.95) 55%, transparent 45%);

      @include breakpoint($large) {
        background-image: linear-gradient(95deg, rgba($live-blue, 0.95) 55%, transparent 45%);
      }

      @include breakpoint($xlarge) {
        background-image: linear-gradient(97.5deg, rgba($live-blue, 0.95) 55%, transparent 45%);
      }
    }

    &.tertiary.doubled.left::before {
      background-image: linear-gradient(175deg, rgba($live-blue, 0.75) 55%, transparent 45%),
        linear-gradient(185deg, rgba($live-blue, 0.75) 55%, transparent 45%);

      @include breakpoint($large) {
        background-image: linear-gradient(85deg, rgba($live-blue, 0.75) 55%, transparent 45%),
          linear-gradient(95deg, rgba($live-blue, 0.75) 55%, transparent 45%);
      }

      @include breakpoint($xlarge) {
        background-image: linear-gradient(82.5deg, rgba($live-blue, 0.75) 55%, transparent 45%),
          linear-gradient(97.5deg, rgba($live-blue, 0.75) 55%, transparent 45%);
      }
    }

    &.tertiary.bottom.right::before {
      background-image: linear-gradient(5deg, rgba($live-blue, 0.95) 55%, transparent 45%);

      @include breakpoint($large) {
        background-image: linear-gradient(-85deg, rgba($live-blue, 0.95) 55%, transparent 45%);
      }

      @include breakpoint($xlarge) {
        background-image: linear-gradient(-82.5deg, rgba($live-blue, 0.95) 55%, transparent 45%);
      }
    }

    &.tertiary.top.right::before {
      background-image: linear-gradient(-5deg, rgba($live-blue, 0.95) 55%, transparent 45%);

      @include breakpoint($large) {
        background-image: linear-gradient(-95deg, rgba($live-blue, 0.95) 55%, transparent 45%);
      }

      @include breakpoint($xlarge) {
        background-image: linear-gradient(-97.5deg, rgba($live-blue, 0.95) 55%, transparent 45%);
      }
    }

    &.tertiary.doubled.right::before {
      background-image: linear-gradient(5deg, rgba($live-blue, 0.75) 55%, transparent 45%),
        linear-gradient(-5deg, rgba($live-blue, 0.75) 55%, transparent 45%);

      @include breakpoint($large) {
        background-image: linear-gradient(-85deg, rgba($live-blue, 0.75) 55%, transparent 45%),
          linear-gradient(-95deg, rgba($live-blue, 0.75) 55%, transparent 45%);
      }

      @include breakpoint($xlarge) {
        background-image: linear-gradient(-82.5deg, rgba($live-blue, 0.75) 55%, transparent 45%),
          linear-gradient(-97.5deg, rgba($live-blue, 0.75) 55%, transparent 45%);
      }
    }

    &-colored {
      flex: 1 0 100%;
      height: 50%;

      @include breakpoint($large) {
        flex: 1 0;
        height: 100%;
      }

      &.right {
        order: 2;
      }

      &.primary {
        color: $white-gray;
      }

      &-text {
        margin: auto 0.75rem;
        text-shadow: 0.125rem 0.125rem rgba($white-gray, 0.2);

        @include breakpoint($medium) {
          margin: auto 1rem;
        }

        @include breakpoint($large) {
          display: flex;
          flex-direction: column;
          margin: auto 3rem;
        }

        &-title {
          border-bottom: 0.05rem solid $dark-gray;
          font-size: 1.75rem;
          margin-bottom: 0;
          margin-top: 1.5rem;

          @include breakpoint($large) {
            margin-bottom: 1rem;
            margin-top: 2rem;
          }

          @include breakpoint($xlarge) {
            font-size: 2rem;
          }

          &.primary {
            border-bottom-color: $white-gray;
          }
        }

        &-paragraph {
          margin-bottom: 1rem;

          @include breakpoint($small) {
            font-size: 1.25rem;
          }
        }
      }
    }

    &-transparent {
      flex: 1 0 100%;
      height: 50%;

      @include breakpoint($large) {
        flex: 1 0;
        height: 100%;
      }

      &.right {
        order: 1;
      }

      &-image {
        margin: auto;
        margin-bottom: 0;
        max-height: 85%;
        max-width: 85%;

        @include breakpoint($large) {
          margin-bottom: auto;
          max-height: 100%;
          max-width: 100%;
        }
      }
    }
  }
}
</style>
